import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ChevronLeft, ChevronRight, Send, Sparkles } from 'lucide-react';

/**
 * Single question step in the readiness assessment flow. Renders the
 * ISO 27001:2022 theme badge, maturity-level options (localized), an optional
 * context textarea, and Back/Next (or Submit on the final question) controls.
 */
export function QuestionStep({
  step,
  totalSteps,
  currentQ,
  selections,
  setSelections,
  contexts,
  setContexts,
  isLastQuestion,
  canProceed,
  onBack,
  onNext,
  onSubmit,
}) {
  const { t } = useTranslation();
  const qid = currentQ?.id;
  const themeLabel = currentQ?.theme ? t('iso2022.themes.' + currentQ.theme) : '';

  return (
    <Card className="bg-white border-[#DDE1E5]" data-testid="question-step">
      <CardContent className="p-6 space-y-5">
        <div className="text-center mb-2">
          <div className="text-xs text-[#8A929B] uppercase tracking-wider mb-2">
            {t('assessment.stepOf', { current: step + 1, total: totalSteps })}
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <span className="inline-block px-2 py-0.5 rounded text-xs bg-blue-500/10 text-[#527F19] font-medium" data-testid="question-theme-badge">
              {themeLabel}{currentQ?.controls ? ` · ${currentQ.controls}` : ''}
            </span>
            {currentQ?.is_new && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-[#2867B2]/10 text-[#2867B2] font-medium" data-testid="question-new-badge">
                <Sparkles className="w-3 h-3" /> {t('iso2022.new')}
              </span>
            )}
          </div>
        </div>
        <h2 className="text-base font-bold text-center text-[#171A1D] leading-snug" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="question-text">
          {t('assessment.questions.' + qid + '.question')}
        </h2>

        {/* Maturity options */}
        <div className="space-y-2" data-testid="maturity-options">
          {(currentQ?.options || []).map(opt => {
            const isSelected = selections[currentQ.id] === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSelections(prev => ({ ...prev, [currentQ.id]: opt.value }))}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  isSelected ? 'border-[#527F19] bg-blue-50' : 'border-[#DDE1E5] bg-[#F5F6F7] hover:border-[#DDE1E5]'
                }`}
                data-testid={`option-${opt.value}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    isSelected ? 'border-[#527F19]' : 'border-[#DDE1E5]'
                  }`}>
                    {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#527F19]" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                        isSelected ? 'bg-[#527F19]/20 text-[#527F19]' : 'bg-[#F5F6F7] text-[#8A929B]'
                      }`}>L{opt.level}</span>
                      <span className={`text-sm ${isSelected ? 'text-[#171A1D] font-medium' : 'text-[#66707A]'}`}>
                        {t('assessment.questions.' + qid + '.options.' + opt.value)}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Optional context */}
        <div>
          <Label className="text-[#8A929B] text-xs mb-1.5 block">{t('assessment.additionalContext')}</Label>
          <Textarea
            placeholder={t('assessment.contextPlaceholder')}
            value={contexts[currentQ?.id] || ''}
            onChange={e => setContexts(prev => ({ ...prev, [currentQ.id]: e.target.value }))}
            className="min-h-[72px] bg-white border-[#DDE1E5] text-[#171A1D] text-sm placeholder:text-[#DDE1E5]"
            data-testid="context-textarea"
          />
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 h-11 border-[#DDE1E5] text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7]"
            onClick={onBack}
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> {t('assessment.back')}
          </Button>
          {isLastQuestion ? (
            <Button
              className="flex-1 h-11 bg-[#527F19] hover:bg-[#3F6114] text-white font-semibold"
              disabled={!canProceed}
              onClick={onSubmit}
              data-testid="submit-btn"
            >
              <Send className="w-4 h-4 mr-1" /> {t('assessment.getAnalysis')}
            </Button>
          ) : (
            <Button
              className="flex-1 h-11 bg-[#527F19] hover:bg-[#3F6114] text-white font-semibold"
              disabled={!canProceed}
              onClick={onNext}
              data-testid="next-btn"
            >
              {t('assessment.next')} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
